import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import SearchableSelect from "./SearchableSelect";

export default function EditFinanceModal({ isOpen, onClose, onSubmit, data }) {
  const [formData, setFormData] = useState({
    name: "",
    status: "ACTIVE",
    type: "PAYMENT_ACCOUNT",
    payment_methods: {
      TRANSFER: false,
      QRIS: false,
      CAMPURAN: false,
    },
    show_to_cashier: false,
  });

  useEffect(() => {
    if (data) {
      const raw = (data.payment_methods || data.description || "").toUpperCase();

      setFormData({
        name: data.name || "",
        status: data.status === "Aktif" ? "ACTIVE" : data.status === "Non-Aktif" ? "INACTIVE" : data.status || "ACTIVE",
        type: data.type || "PAYMENT_ACCOUNT",
        payment_methods: {
          TRANSFER: raw.includes("TRANSFER"),
          QRIS: raw.includes("QRIS"),
          CAMPURAN: raw.includes("CAMPURAN"),
        },
        show_to_cashier: data.show_to_cashier || false,
      });
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleMethodToggle = (method) => {
    setFormData((prev) => ({
      ...prev,
      payment_methods: {
        ...prev.payment_methods,
        [method]: !prev.payment_methods[method],
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const selectedMethods = Object.entries(formData.payment_methods)
      .filter(([, selected]) => selected)
      .map(([method]) => method);

    onSubmit({
      ...data,
      name: formData.name,
      status: formData.status,
      type: formData.type,
      payment_methods: formData.type === "PAYMENT_ACCOUNT" && formData.name?.toLowerCase() !== "laci kasir" ? selectedMethods.join(",") : "",
      show_to_cashier: formData.show_to_cashier,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Data Keuangan">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Nama Kas/Rekening */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">
            Nama Kas/Rekening <span className="text-rose-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Contoh: Mandiri 12345789 Legiun Diesel"
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
            required
          />
        </div>

        {/* Type & Status - Row */}
        <div className="grid grid-cols-2 gap-4">
          {/* Type (Tipe Akun) */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Tipe Akun <span className="text-rose-500">*</span>
            </label>
            <SearchableSelect
              options={[
                { value: "PAYMENT_ACCOUNT", label: "Kas/Rekening Pembayaran" },
                {
                  value: "OPERATIONAL_ACCOUNT",
                  label: "Kas/Rekening Operasional",
                },
              ]}
              value={formData.type}
              onChange={(value) =>
                handleChange({ target: { name: "type", value } })
              }
              placeholder="Pilih tipe akun..."
              displayKey="label"
              valueKey="value"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Status <span className="text-rose-500">*</span>
            </label>
            <SearchableSelect
              options={[
                { value: "ACTIVE", label: "Aktif" },
                { value: "INACTIVE", label: "Non-Aktif" },
              ]}
              value={formData.status}
              onChange={(value) =>
                handleChange({ target: { name: "status", value } })
              }
              placeholder="Pilih status..."
              displayKey="label"
              valueKey="value"
            />
          </div>
        </div>

        {/* Metode Pembayaran (hanya tampil apabila Tipe Akun = Kas/Rekening Pembayaran dan bukan Laci Kasir) */}
        {formData.type === "PAYMENT_ACCOUNT" && formData.name?.toLowerCase() !== "laci kasir" && (
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Metode Pembayaran <span className="text-rose-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: "TRANSFER", label: "Transfer" },
                { key: "QRIS", label: "QRIS" },
                { key: "CAMPURAN", label: "Campuran" },
              ].map((item) => {
                const checked = formData.payment_methods[item.key] || false;
                return (
                  <div
                    key={item.key}
                    onClick={() => handleMethodToggle(item.key)}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border cursor-pointer select-none transition-colors ${
                      checked
                        ? "bg-white border-teal-500 ring-2 ring-teal-500/20 shadow-sm"
                        : "bg-slate-50/70 border-slate-200 hover:bg-slate-100/70"
                    }`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {}}
                      className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer pointer-events-none"
                    />
                    <span className="text-sm font-medium text-slate-700">
                      {item.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Akses Kasir Toggle Switch */}
        <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200/80 rounded-xl">
          <div>
            <label
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  show_to_cashier: !prev.show_to_cashier,
                }))
              }
              className="text-sm font-medium text-slate-800 cursor-pointer block">
              Tampilkan di role Kasir
            </label>
            <p className="text-xs text-slate-500 mt-0.5">
              Kasir dapat memilih rekening ini saat transaksi
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={formData.show_to_cashier || false}
            onClick={() =>
              setFormData((prev) => ({
                ...prev,
                show_to_cashier: !prev.show_to_cashier,
              }))
            }
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
              formData.show_to_cashier ? "bg-teal-600" : "bg-slate-300"
            }`}>
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                formData.show_to_cashier ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium text-sm">
            Batal
          </button>
          <button
            type="submit"
            className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm shadow-sm shadow-teal-600/20">
            Simpan Perubahan
          </button>
        </div>
      </form>
    </Modal>
  );
}
